import { useContext } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  CollapsibleSection,
  Header,
  Key,
  BubbleKey,
  Row,
  Rows,
  Section,
  Specifications as List,
  Subheader,
  Value
} from '../Specifications';

import * as FedExDeliveryContent from '../../popups/content/FedExDelivery';
import * as TotalSettingCaratWeightContent from '../../popups/content/specifications/setting/TotalCaratWeight';
import * as MetalWeightContent from '../../popups/content/specifications/setting/MetalWeight';
import * as CenterWidthContent from '../../popups/content/specifications/setting/CenterWidth';
import * as ShoulderWidthContent from '../../popups/content/specifications/setting/ShoulderWidth';
import * as CenterHeightContent from '../../popups/content/specifications/setting/CenterHeight';
import * as BaseDepthContent from '../../popups/content/specifications/setting/BaseDepth';
import * as BaseWidthContent from '../../popups/content/specifications/setting/BaseWidth';
import * as SizesAvailableContent from '../../popups/content/specifications/setting/SizesAvailable';
import * as MatchingBandContent from '../../popups/content/specifications/setting/MatchingBand';

import * as NumberContent from '../../popups/content/specifications/accent_stones/Number';
import * as ShapeContent from '../../popups/content/specifications/accent_stones/Shape';
import * as TotalAccentCaratWeightContent from '../../popups/content/specifications/accent_stones/TotalCaratWeight';
import * as ColorContent from '../../popups/content/specifications/accent_stones/Color';
import * as ClarityContent from '../../popups/content/specifications/accent_stones/Clarity';

import { SettingStateContext } from '../../../contexts/setting';

import * as routes from '@src/app_routes/settings';
import * as bandRoutes from '@src/app_routes/bands';

import { linkBlueText } from '@src/styles/general';

import QuestionMarkPopup from '@components/popups/QuestionMark';
import * as SetShownWithContent from '@components/popups/content/ShownSetWith';

import ColoredFedEx from '../../ColoredFedEx';

import type { TSettingDetail } from '@types/setting';
import type { ObjectType } from '../../../types/misc';

const totalWidth = setting => {
  if (setting.total_width) {
    return `${Number(setting.total_width).toFixed(2)} mm`;
  }
};

const shankThickness = setting => {
  if (setting.shank_thickness) {
    return `${Number(setting.shank_thickness).toFixed(2)} mm`;
  }
};

const baseWidth = setting => {
  if (setting.shank_base_width) {
    return `${Number(setting.shank_base_width).toFixed(2)} mm`;
  }
};

const shoulderWidth = setting => {
  if (setting.shoulder_width) {
    return `${Number(setting.shoulder_width).toFixed(2)} mm`;
  }
};

const centerHeight = setting => {
  if (setting.center_height) {
    return `${Number(setting.center_height).toFixed(2)} mm`;
  }
};

const metalWeight = setting => {
  if (setting.gram_weight) {
    return `${Number(setting.gram_weight).toFixed(2)} gm`;
  }
};

function MainSection({
  setting,
  hideHeader
}: {
  setting: TSettingDetail,
  hideHeader: boolean
}) {
  const totalWidthValue = totalWidth(setting);
  const shankThicknessValue = shankThickness(setting);
  const baseWidthValue = baseWidth(setting);
  const shoulderWidthValue = shoulderWidth(setting);

  return (
    <Section>
      {hideHeader ? null : <Header>Setting Details</Header>}
      <Rows>
        <Row>
          <Key>Item Number</Key>
          <Value>{setting.item_number}</Value>
        </Row>
        {setting.metal && (
          <Row>
            <Key>Metal</Key>
            <Value>{setting.metal}</Value>
          </Row>
        )}
        {setting.total_carat_weight ? (
          <Row>
            <BubbleKey content={TotalSettingCaratWeightContent}>
              Total Ct. Wt.
            </BubbleKey>
            <Value>{Number(setting.total_carat_weight).toFixed(2)} ct.</Value>
          </Row>
        ) : null}
        {setting.gram_weight ? (
          <Row>
            <BubbleKey content={MetalWeightContent}>Metal Weight</BubbleKey>
            <Value>{metalWeight(setting)}</Value>
          </Row>
        ) : null}
        {totalWidthValue ? (
          <Row>
            <BubbleKey content={CenterWidthContent}>Center Width</BubbleKey>
            <Value>{totalWidthValue}</Value>
          </Row>
        ) : null}
        {shoulderWidthValue ? (
          <Row>
            <BubbleKey content={ShoulderWidthContent}>Shoulder Width</BubbleKey>
            <Value>{shoulderWidthValue}</Value>
          </Row>
        ) : null}
        {shankThicknessValue && BaseWidthContent ? (
          <Row>
            <BubbleKey content={BaseWidthContent}>Base Width</BubbleKey>
            <Value>{baseWidthValue}</Value>
          </Row>
        ) : null}
        {setting.center_height ? (
          <Row>
            <BubbleKey content={CenterHeightContent}>Center Height</BubbleKey>
            <Value>{centerHeight(setting)}</Value>
          </Row>
        ) : null}
        {shankThicknessValue ? (
          <Row>
            <BubbleKey content={BaseDepthContent}>Base Depth</BubbleKey>
            <Value>{shankThicknessValue}</Value>
          </Row>
        ) : null}
        {setting.size_range && (
          <Row>
            <BubbleKey content={SizesAvailableContent}>
              Sizes Available
            </BubbleKey>
            <Value>{setting.size_range}</Value>
          </Row>
        )}
        {setting.matching_band_sku ? (
          <Row>
            <BubbleKey content={MatchingBandContent}>Matching Band</BubbleKey>
            <Value>
              <a
                href={bandRoutes.detailPath(
                  { vendor_sku: setting.matching_band_sku },
                  { matching_sku: setting.vendor_sku }
                )}
                title='See matching band'
                target='_blank'
              >
                {setting.matching_band_item_number}
              </a>
            </Value>
          </Row>
        ) : null}
      </Rows>
    </Section>
  );
}

function SetWithSection({ setting }: { setting: TSettingDetail }) {
  return (
    <Section>
      <Subheader>
        Shown set with:
        <QuestionMarkPopup
          initialContent={SetShownWithContent}
          containerStyle={{ float: 'right' }}
          style={{ marginRight: '5px' }}
        />
      </Subheader>
      <Rows>
        <Row>
          <Key>{setting.shown_set_with_shape}</Key>
          <Value>{setting.shown_set_with_size}</Value>
        </Row>

        {setting.hasOwnProperty('stone_size_range_in_current_shape') ? (
          <Row>
            <Key>(compatible sizes)</Key>
            <Value>
              {setting.stone_size_range_in_current_shape
                .sort()
                .map(size => Number(size).toFixed(2))
                .join(' - ')}{' '}
              ct.
            </Value>
          </Row>
        ) : null}
      </Rows>
    </Section>
  );
}

function OtherShapesSection({ setting }: { setting: TSettingDetail }) {
  const dispatch = useDispatch();

  if (!setting.other_shapes) return null;

  const shapesWithoutCurrent = setting.other_shapes.filter(shape => {
    return shape.stone_shape !== setting.shown_set_with_shape;
  });

  if (shapesWithoutCurrent.length === 0) return null;

  function visitShape(e, shape, url) {
    if (typeof window === 'undefined') return;

    e.preventDefault();

    dispatch({
      type: 'SET_SETTING_FILTER',
      payload: {
        filterName: 'shape',
        value: shape.toLowerCase()
      }
    });

    window.location = url;
  }

  return (
    <Section>
      <Subheader>Can also be set with:</Subheader>
      <Rows>
        <Row>
          <div style={{ color: linkBlueText }}>
            {shapesWithoutCurrent.map((shape, i) => {
              return [
                i > 0 && ', ',
                <a
                  key={shape.stone_shape}
                  href={routes.detailPath({
                    vendor_sku: shape.vendor_sku,
                    url_segment_description: shape.url_segment_description
                  })}
                  onClick={e =>
                    visitShape(
                      e,
                      shape.stone_shape,
                      routes.detailPath({
                        vendor_sku: shape.vendor_sku,
                        url_segment_description: shape.url_segment_description
                      })
                    )
                  }
                >
                  {shape.stone_shape}
                </a>
              ];
            })}
          </div>
        </Row>
      </Rows>
    </Section>
  );
}

function AccentStonesSection({
  stones,
  showNumber,
  number
}: {
  stones: ObjectType,
  number: number,
  showNumber: boolean
}) {
  return (
    <CollapsibleSection>
      <Subheader>Accent Diamonds{showNumber && ` (Group ${number})`}</Subheader>
      <Rows>
        <Row>
          <BubbleKey content={NumberContent}>Number</BubbleKey>
          <Value>{stones.quantity}</Value>
        </Row>
        <Row>
          <BubbleKey content={ShapeContent}>Shape</BubbleKey>
          <Value>{stones.shape}</Value>
        </Row>
        <Row>
          <BubbleKey content={TotalAccentCaratWeightContent}>
            Total Ct. Wt.
          </BubbleKey>
          <Value>{stones.total_carat_weight_string}</Value>
        </Row>
        {stones.minimum_color && (
          <Row>
            <BubbleKey content={ColorContent}>Min. Color</BubbleKey>
            <Value>{stones.minimum_color}</Value>
          </Row>
        )}
        {stones.minimum_clarity && (
          <Row>
            <BubbleKey content={ClarityContent}>Min. Clarity</BubbleKey>
            <Value>{stones.minimum_clarity}</Value>
          </Row>
        )}
      </Rows>
    </CollapsibleSection>
  );
}

function OrderDetailsSection({ setting }: { setting: TSettingDetail }) {
  return (
    <Section>
      <Rows>
        <Row>
          <Key>Returns</Key>
          <Value>
            <span>30 days</span>
          </Value>
        </Row>
        <Row>
          <Key>Comes with</Key>
          <Value>
            Appraisal
            <br />
            Presentation box
          </Value>
        </Row>
        <Row>
          <BubbleKey content={FedExDeliveryContent}>Shipping</BubbleKey>
          <Value>
            <span>
              <ColoredFedEx />
            </span>
          </Value>
        </Row>
        <Row>
          <Key>Delivery Date</Key>
          <Value>
            <span>{setting.delivery_date}</span>
          </Value>
        </Row>
      </Rows>
    </Section>
  );
}

export default function Specs({ hideHeader }: { hideHeader: boolean }) {
  const { setting }: { setting: TSettingDetail } = useContext(
    SettingStateContext
  );
  const diamondId = useSelector(state => state.ringBuilder.diamond.id);

  if (!setting) return null;

  return (
    <List
      allowExpansion={setting.preset_stones && setting.preset_stones.length > 0}
    >
      <MainSection setting={setting} hideHeader={hideHeader} />
      {!diamondId && <SetWithSection setting={setting} />}
      {!diamondId && <OtherShapesSection setting={setting} />}
      {setting.preset_stones
        ? setting.preset_stones.map((ps: ObjectType, i: number) => {
            return (
              <AccentStonesSection
                stones={ps}
                key={`stones_${i}`}
                number={i + 1}
                showNumber={setting.preset_stones.length > 1}
              />
            );
          })
        : null}
      <OrderDetailsSection setting={setting} />
    </List>
  );
}
